import React, { Component, ReactNode, useCallback } from 'react'
import styles from './factory.module.sass'
import { HistoryAction, HistoryData, HistoryResources } from '../../../../../responses/history'
import UserPanel from '../../../../data-components/elements/UserElement/UserElement'
import { Priority } from '../../../../data-components/pickers/PriorityPicker/PriorityPicker'
import { parseDate, toDateString } from '../../../../../utils/date-utils'
import { identity, pipe } from 'fp-ts/es6/function'
import { Tender, TenderStatus } from '../../../../data-components/pickers/TenderPicker/TenderPicker'
import { ProjectStatus, projectStatusToColor, relatedToString } from '../../../../../responses/projects'
import { HistoryType, TaskStatus } from '../../../../../constants/enums'
import Html from '../../../../etc/Html/Html'
import Album from '../../../../data-components/files/Album/Album'
import { useRootDispatch } from '../../../../../utils/hooks'
import { EmptyObject, ID } from '../../../../../types/UtilityTypes'
import { User } from '../../../../../responses/users'
import UpdateElement from './UpdateElement/UpdateElement'
import TagElement from '../../../../data-components/tags/TagElement/TagElement'
import TaskOpenIcon from 'files/task-open.svg'
import TaskCloseIcon from 'files/task-closed.svg'
import SwitchVertical from 'files/switch-vertical.svg'
import SwitchHorizontal from 'files/switch-horizontal.svg'
import UserAddIcon from 'files/user/user-add.svg'
import UserRemoveIcon from 'files/user/user-remove.svg'
import DescriptionIcon from 'files/description.svg'
import RemoveIcon from 'files/delete.svg'
import DeleteIcon from 'files/delete.svg'
import PlusIcon from 'files/plus.svg'
import TagAddIcon from 'files/tag/tag-add.svg'
import TagRemoveIcon from 'files/tag/tag-remove.svg'
import CalendarIcon from 'files/calendar.svg'
import AttachIcon from 'files/attach.svg'
import DetachIcon from 'files/detach.svg'
import ObjectName from '../../../../etc/ObjectName/ObjectName'
import CommentElement from '../../../../data-components/elements/CommentElement/CommentElement'
import * as A from 'fp-ts/es6/Array'
import * as R from 'fp-ts/es6/Record'
import * as O from 'fp-ts/es6/Option'
import { galleryActions } from '../../../../../redux/global/gallery/gallery-actions'
import DesignStatusElement from '../../../../data-components/elements/DesignStatusElement/DesignStatusElement'
import { FileData } from '../../../../../responses/files'
import { Comment } from '../../../../../responses/comments'
import { FactoryRenderer, FactoryRendererProps, LastRenderer, SimpleRenderer, TextRenderer } from './factory-types'
import { DesignStatus, ObjectStatus } from '../../../../../responses/objects'

type Config = {
    [type in HistoryType]: Partial<Record<string, FactoryRenderer>>
}

const DefaultHistoryRenderer: FactoryRenderer = props => <div className={styles.line}>
    <div/>
    <div>
        {props.action.text}<br/>
        type:{props.data.type}<br/>
        actionType:{props.action.type}<br/>
        key:{props.action.key}<br/>
        values:{props.action.values.join(', ')}
    </div>
</div>

interface BoundaryState {
    error?: {
        text: string,
        stack?: string,
    },
}

export class HistoryErrorBoundary extends Component<FactoryRendererProps, BoundaryState> {
    constructor(props: FactoryRendererProps) {
        super(props)
        this.state = {
            error: undefined
        }
    }

    static getDerivedStateFromError(error: unknown): BoundaryState {
        console.warn(error)
        return {
            error: {
                text: (error as EmptyObject).toString(),
                stack: '',
            },
        }
    }

    render(): ReactNode {
        if (this.state.error) {
            return <div className={styles.line}>
                <div/>
                <div>
                    При отрисовке события: <br/>
                    {this.props.action.text} <br/>
                    type:{this.props.data.type} <br/>
                    actionType:{this.props.action.type} <br/>
                    key:{this.props.action.key} <br/>
                    values:{this.props.action.values.join(', ')} <br/>
                    Возникло следующее исключение: <br/>
                    {this.state.error.text} <br/><br/>
                </div>
            </div>
        } else {
            return this.props.children
        }
    }
}

const CommentRenderer: FactoryRenderer = ({ resources, data, action, history }) => {
    const dispatch = useRootDispatch()

    const onFileClick = useCallback((current: FileData, comment: Comment) => {
        switch (data.type) {
            case 'task':
                dispatch(galleryActions.open({
                    type: comment.resulted ? 'task' : 'comment',
                    tasksId: [data.task],
                    objectId: data.project_work_object,
                    current,
                }))
                break
            case 'project':
            case 'project_work_object':
                dispatch(galleryActions.open({
                    type: 'single',
                    comment,
                    current,
                }))
        }
    }, [data, dispatch])

    return <>{
        pipe(
            action.values,
            A.map(id => R.lookup(id as string, resources.comment)),
            A.map(O.map(comment => <CommentElement
                key={comment.id}
                comment={comment}
                editable={!!history}
                onFileClick={onFileClick}
            />)),
            A.compact,
        )
    }</>
}

const FilesRenderer: FactoryRenderer = ({ resources, data, action }) => {
    const dispatch = useRootDispatch()

    const files: FileData[] = action.values.map(id => ({
        ...resources.file[id as ID],
        user: resources.user[data.user]
    }))

    return <div className={styles.line}>
        {
            (() => {
                switch (action.type) {
                    case 'add':
                        return <AttachIcon/>
                    case 'delete':
                        return <DetachIcon/>
                    case 'change':
                        return ''
                }
            })()
        }
        <Album
            files={files}
            onClick={current => {
                switch (data.type) {
                    case 'task':
                        dispatch(galleryActions.open({
                            type: 'comment',
                            tasksId: [data.task],
                            objectId: data.project_work_object,
                            current,
                        }))
                        break
                    case 'project':
                    case 'project_work_object':
                        dispatch(galleryActions.open({
                            type: 'single',
                            comment: {
                                id: `${files[0].user.id} ${files[0].created_at}`,
                                created_at: files[0].created_at,
                                resulted: false,
                                owner_id: 0,
                                owner_type: 'project',
                                files,
                                text: '',
                                deleted: false,
                                edited: false,
                                user: files[0].user,
                                user_id: files[0].user.id,
                            },
                            current
                        }))
                }
            }}
        />
    </div>
}

export type FactoryIcons = {
    add?: ReactNode,
    delete?: ReactNode,
    change?: ReactNode,
} | LastRenderer

// eslint-disable-next-line react/display-name
const rendererGenerator = (icons: FactoryIcons, renderer?: SimpleRenderer, text?: TextRenderer): FactoryRenderer => data =>
    <UpdateElement {...data} icons={icons} renderer={renderer} text={text}/>

// eslint-disable-next-line react/display-name
const userGenerator = (icons: FactoryIcons) => (accessor: (res: HistoryResources) => Record<ID, User>) => rendererGenerator(icons, res => id =>
    <UserPanel simple user={accessor(res)[id as ID]}/>)

const defaultUserGenerator = userGenerator({
    add: <UserAddIcon/>,
    delete: <UserRemoveIcon/>,
    change: <SwitchHorizontal/>,
})

const work_type: FactoryRenderer = rendererGenerator({}, res => id => res.work_type[id as ID].code + ' - ' + res.work_type[id as ID].name)


const project_work_object = rendererGenerator({
    add: <PlusIcon/>,
    delete: <DeleteIcon/>
// eslint-disable-next-line react/display-name
}, res => id => <ObjectName object={res.project_work_object[id as ID]}/>)

const dateRenderer = rendererGenerator(() => <CalendarIcon/>, () => content => pipe(content as string, parseDate, toDateString))

const identityRenderer = rendererGenerator(() => <div/>, () => content => identity(content as string))

const designStatusRender = (type: 'design' | 'production') => rendererGenerator(
    status => <DesignStatusElement allowEmpty status={status as DesignStatus} type={type}/>
)

// eslint-disable-next-line react/display-name
const HtmlRenderer = rendererGenerator({}, () => html => <Html html={html as string}/>)

const factoryConfig: Config = {
    project: {
        project: rendererGenerator({}),
        file: FilesRenderer,
        comment: CommentRenderer,
        countries: rendererGenerator({}, res => id => res.countries[id as ID]?.code),
        user: defaultUserGenerator(res => res.user),
        status: rendererGenerator(status => <div
            className={styles.project}
            style={{background: projectStatusToColor(status as ProjectStatus)}}
        />),
        network: rendererGenerator({}, res => id => res.network[id as ID].name),
        finplan: rendererGenerator({}, res => id => relatedToString(res.finplan[id as ID])),
        worksection: identityRenderer,
        project_work_object,
        work_type,
        name: rendererGenerator({}, res => id => res.project[id as ID].name),
        code: identityRenderer,
        direction: rendererGenerator({}, res => id => res.direction[id as ID].title),
        manager: defaultUserGenerator(res => res.manager),
        client: rendererGenerator({}, res => id => res.client[id as ID].short_title),
        brand: rendererGenerator({}, res => id => res.brand[id as ID].title),
        short_name: identityRenderer,
        // eslint-disable-next-line react/display-name
        description: HtmlRenderer,
        opened_at: dateRenderer,
        dir: identityRenderer,
        folder: identityRenderer,
    },
    project_work_object: {
        tag: rendererGenerator({
            add: <TagAddIcon/>,
            delete: <TagRemoveIcon/>
            // eslint-disable-next-line react/display-name
        }, res => id => <TagElement tag={res.tag[id as ID]}/>),
        comment: CommentRenderer,
        task: rendererGenerator({
            add: <PlusIcon/>,
            delete: <RemoveIcon/>,
        }, res => id => res.task[id as ID]?.type.name),
        curator: defaultUserGenerator(res => res.curator),
        description: HtmlRenderer,
        file: FilesRenderer,
        status: rendererGenerator(status => status === ObjectStatus.CLOSED ? <TaskCloseIcon/> : <TaskOpenIcon/>),
        design_status: designStatusRender('design'),
        production_status: designStatusRender('production'),
    },
    task: {
        comment: CommentRenderer,
        file: FilesRenderer,
        deadline_at: dateRenderer,
        text: HtmlRenderer,
        target_user: defaultUserGenerator(res => res.target_user),
        status: rendererGenerator(status => status === TaskStatus.CLOSED ? <TaskCloseIcon/> : <TaskOpenIcon/>),
        type: rendererGenerator(() => <DescriptionIcon/>, res => id => res.type[id as ID].name),
        tender: rendererGenerator(tender => <Tender showDisabled state={tender as TenderStatus}/>),
        priority: rendererGenerator(
            () => <SwitchVertical/>,
            // eslint-disable-next-line react/display-name
            () => priority => <Priority priority={priority as number}/>
        ),
    }
}

/**
 * @throws type exceptions
 */

export const getRenderer = (data: HistoryData, action: HistoryAction): FactoryRenderer => {
    if (data.type === 'task' && ['id', 'parent', 'project_work_object', 'kind_status'].includes(action.key)) {
        return () => null
    }
    return factoryConfig[data.type][action.key] ?? DefaultHistoryRenderer
}

